<!--
  Rider-facing ride page, opened from a shareable link.

  Layout and styling are ported from the corporate portal's UserTripLinkView, so
  a crew member sees the same page whichever portal booked their ride.

  Everything is read from our own Trip and UberTrip collections rather than the
  Uber API, so opening a link costs nothing and the page works regardless of
  what Uber's API is doing. It polls while a car is still coming, since a phone
  on this page has no socket.

  The link is public: it carries no login, and the Uber trip UUID in the URL is
  the only thing protecting it. Anyone the URL reaches can cancel the ride, dial
  the driver, trigger a call to the passenger's phone, and message the driver.
  That was a deliberate choice. The one concession is the passenger's mobile,
  which the server masks to its last four digits.
-->
<template>
  <div class="trip-shell">
    <div class="utv-loading" v-if="loading">
      <v-progress-circular indeterminate color="#111111" size="42" width="3"></v-progress-circular>
    </div>

    <div class="utv-dead" v-else-if="!trip">
      <v-icon size="46" color="#c9c9c9">mdi-link-variant-off</v-icon>
      <p class="utv-dead-title">This crew link is not valid</p>
      <p class="utv-dead-sub">{{ error || "The link may be mistyped, or the booking no longer exists." }}</p>
    </div>

    <template v-else>
      <!-- Header: status -->
      <div class="trip-header">
        <div class="header-left">
          <!-- Both references up front: ours is what the crew quotes to the
               office, Uber's is what Uber support asks for. -->
          <div class="id-row">
            <span class="id-item">
              <span class="id-key">Trip ID</span>
              {{ trip.displayId }}
            </span>
            <span class="id-item">
              <span class="id-key">Uber ID</span>
              {{ trip.uberTripId }}
            </span>
          </div>
          <div class="status-badge">
            <span class="pulse-dot" v-if="rideAhead"></span>
            {{ statusLabel(trip.status) }}
          </div>
          <div class="status-sub" v-if="statusNarrative">{{ statusNarrative }}</div>
        </div>
        <!-- Only once a driver is assigned - the PIN is what the crew reads to
             them, so before that there is nobody to read it to. -->
        <div class="header-right" v-if="showPin">
          <div class="otp-pill">PIN {{ trip.guest.otp }}</div>
        </div>
      </div>

      <!-- Clock times, computed on the server from its own clock - a phone in
           another timezone must not shift them. -->
      <div class="meta-grid" v-if="rideAhead">
        <div class="meta-card">
          <div class="meta-label">Pickup By</div>
          <div class="meta-value">{{ trip.pickupEtaAt || "—" }}</div>
          <div class="meta-sub">at {{ shortPlace(trip.pickup) }}</div>
        </div>
        <div class="meta-card">
          <div class="meta-label">Drop By</div>
          <div class="meta-value">{{ trip.dropEtaAt || "—" }}</div>
          <div class="meta-sub">at {{ shortPlace(trip.dropoff) }}</div>
        </div>
      </div>

      <!-- Route -->
      <div class="card">
        <div class="section-label">Route</div>
        <div class="route-row">
          <div class="route-line">
            <span class="dot dot-green"></span>
            <div class="route-connector"></div>
            <span class="dot dot-red"></span>
          </div>
          <div class="route-stops">
            <div class="stop">
              <div class="stop-label">Pickup</div>
              <div class="stop-title">{{ shortPlace(trip.pickup) }}</div>
              <div class="stop-sub">{{ addressOf(trip.pickup) }}</div>
            </div>
            <div class="stop">
              <div class="stop-label">Drop</div>
              <div class="stop-title">{{ shortPlace(trip.dropoff) }}</div>
              <div class="stop-sub">{{ addressOf(trip.dropoff) }}</div>
            </div>
          </div>
        </div>

        <div class="route-meta" v-if="trip.pickupTime">
          <span class="meta-pill">
            <v-icon size="12" color="#111111">mdi-calendar-clock</v-icon>
            Pickup {{ formatDateTime(trip.pickupTime) }}
          </span>
        </div>
      </div>

      <!-- Driver. Only while a car is still coming: once the ride is over the
           crew has no use for who drove it, and the card would sit there with
           every button already hidden. -->
      <div class="card" v-if="driverDetailsVisible && rideAhead">
        <div class="section-label">Driver</div>
        <div class="driver-row">
          <div class="driver-avatar">
            <v-icon size="24" color="#ffffff">mdi-account</v-icon>
          </div>
          <div class="driver-info">
            <div class="driver-name">{{ (trip.driver && trip.driver.name) || "Assigning…" }}</div>
            <div class="driver-rating" v-if="trip.driver && trip.driver.rating">
              <span class="stars">{{ starString(trip.driver.rating) }}</span>
              <span class="rating-val">{{ trip.driver.rating }}</span>
            </div>
            <!-- Number hidden. Direct Call below still dials it, so the number
                 is only off the page, not out of the payload. -->
            <!-- <div class="driver-phone">{{ (trip.driver && trip.driver.phoneNumber) || "—" }}</div> -->
          </div>
        </div>

        <div class="driver-actions" v-if="hasDriverAssigned">
          <!-- Direct dials the driver from this device, so the passenger's own
               number is shown to them. Uber Call goes through Uber and is
               anonymous - the labels have to make that difference obvious. -->
          <button
            class="action-btn action-btn--direct"
            :disabled="!(trip.driver && trip.driver.phoneNumber) || callingDirect"
            @click="callDirect"
          >
            <v-icon size="14" color="currentColor">mdi-phone</v-icon>
            Direct Call
          </button>
          <button class="action-btn action-btn--uber" :disabled="callingUber" @click="callViaUber">
            <v-icon size="14" color="#ffffff">mdi-phone-forward</v-icon>
            {{ callingUber ? "Calling…" : "Uber Call" }}
          </button>
        </div>

        <div class="driver-chat" v-if="canMessage">
          <button class="action-btn action-btn--chat" @click="openChat">
            <v-icon size="14" color="currentColor">mdi-message-text-outline</v-icon>
            Message driver
          </button>
        </div>
      </div>

      <!-- Chat -->
      <v-dialog v-model="chatOpen" max-width="520">
        <div class="chat-modal">
          <div class="chat-modal-header">
            <div>
              <div class="chat-modal-title">Chat with driver</div>
              <div class="chat-modal-sub">{{ (trip.driver && trip.driver.name) || "Driver" }}</div>
            </div>
            <button class="chat-modal-close" @click="closeChat">Close</button>
          </div>

          <div class="chat-thread" ref="chatThread">
            <div v-if="messagesLoading" class="chat-loading">Loading conversation…</div>
            <div v-else-if="messages.length === 0" class="chat-empty">
              No messages yet. Say hello to your driver.
            </div>
            <div v-else class="chat-bubbles">
              <div
                v-for="(msg, i) in messages"
                :key="msg.messageId || i"
                class="chat-bubble"
                :class="msg.sender === 'driver' ? 'chat-bubble--in' : 'chat-bubble--out'"
              >
                <!-- Three possible senders, so each line says who wrote it. -->
                <div class="chat-who">{{ senderLabel(msg.sender) }}</div>
                <div class="chat-text">{{ msg.text }}</div>
                <div class="chat-time">{{ msg.timestamp }}</div>
              </div>
            </div>
          </div>

          <div class="chat-composer">
            <textarea
              v-model="messageDraft"
              class="chat-input"
              rows="2"
              :disabled="messagesSending"
              placeholder="Type a message to the driver…"
              @keydown.enter.exact.prevent="sendMessage"
            ></textarea>
            <button
              class="chat-send"
              :disabled="!messageDraft.trim() || messagesSending"
              @click="sendMessage"
            >
              {{ messagesSending ? "…" : "Send" }}
            </button>
          </div>
          <div class="chat-hint">Press Enter to send</div>
        </div>
      </v-dialog>

      <!-- Vehicle. Dropped with the driver card once the ride is over, for the
           same reason: it describes a car that has already gone. -->
      <div class="card" v-if="trip.vehicle && rideAhead">
        <div class="section-label">Vehicle</div>
        <div class="vehicle-row">
          <div class="vehicle-info">
            <div class="vehicle-name">{{ vehicleDisplay }}</div>
            <div class="vehicle-color" v-if="trip.vehicle.color">{{ trip.vehicle.color }}</div>
            <span class="plate" v-if="trip.vehicle.licensePlate">{{ trip.vehicle.licensePlate }}</span>
          </div>
        </div>
      </div>

      <!-- Passenger -->
      <div class="card" v-if="trip.guest">
        <div class="section-label">Passenger</div>
        <div class="guest-row">
          <div class="guest-avatar">{{ guestInitials }}</div>
          <div class="guest-info">
            <div class="guest-name">{{ trip.guest.name || "-" }}</div>
            <div class="guest-phone">{{ trip.guest.mobileMasked }}</div>
          </div>
        </div>
      </div>

      <!-- CTA -->
      <div class="cta-stack" v-if="rideAhead">
        <a
          class="cta-track"
          :href="trip.trackingUrl || '#'"
          target="_blank"
          rel="noopener noreferrer"
          :class="{ disabled: !trip.trackingUrl }"
          :aria-disabled="!trip.trackingUrl"
        >
          <v-icon size="16" color="#ffffff">mdi-crosshairs-gps</v-icon>
          Track live location
        </a>
        <button class="cta-cancel" :disabled="cancelling" v-if="cancellable" @click="cancelRide">
          <v-icon size="14" color="currentColor">mdi-close</v-icon>
          {{ cancelling ? "Cancelling…" : "Cancel trip" }}
        </button>
      </div>

      <!-- Once the ride is over the CTA stack above disappears with it, and the
           page has nothing to act on. This is the one thing still worth doing. -->
      <div class="cta-stack" v-if="reportable">
        <button class="cta-cancel" @click="openIssue">
          <v-icon size="14" color="currentColor">mdi-alert-circle-outline</v-icon>
          Report an issue
        </button>
      </div>

      <!-- Report issue. Built on the chat modal rather than UberConfirm, which
           is confirm-only and has nowhere to type. -->
      <v-dialog v-model="issueOpen" max-width="520">
        <div class="chat-modal">
          <div class="chat-modal-header">
            <div>
              <div class="chat-modal-title">Report an issue</div>
              <div class="chat-modal-sub">Trip {{ trip.displayId || trip.tripId }}</div>
            </div>
            <div class="chat-modal-close" @click="issueOpen = false">Close</div>
          </div>

          <div class="issue-types">
            <button
              v-for="option in issueTypes"
              :key="option.value"
              class="issue-type"
              :class="{ 'issue-type--on': issueForm.issueType === option.value }"
              @click="issueForm.issueType = option.value"
            >
              {{ option.label }}
            </button>
          </div>

          <textarea
            v-model="issueForm.description"
            class="chat-input issue-text"
            rows="4"
            maxlength="2000"
            placeholder="Tell us what happened"
          ></textarea>

          <div class="chat-composer">
            <button class="chat-send issue-submit" :disabled="!canSubmitIssue" @click="submitIssue">
              {{ submittingIssue ? "Sending…" : "Submit" }}
            </button>
          </div>
          <div class="chat-hint">Our team will look into it and get back to you.</div>
        </div>
      </v-dialog>

      <!-- The Uber reference moved to the header, so only the booking time is
           left down here. -->
      <div class="request-meta">
        <span>Booked {{ formatDateTime(trip.bookedAt) }}</span>
      </div>
    </template>

    <UberConfirm ref="confirm"></UberConfirm>
  </div>
</template>

<script>
import axios from "@/axios/axios-global";
import moment from "moment";
import toast from "@/components/common/toast";
import { readApiError } from "./apiError";
import UberConfirm from "./UberConfirm.vue";
import {
  statusLabel,
  hasLiveTracking,
  canMessageDriver,
  isCancellable,
  canReportIssue,
  shouldShowDriverDetails,
  ISSUE_TYPES,
  // Aliased: this component already has a hasDriverAssigned computed, which
  // additionally requires the driver's details to have arrived.
  hasDriverAssigned as driverAssignedStatus,
} from "@/config/uberStatus";

// A phone on this page has no socket, so it polls - often enough to feel live
// while a car is coming, and not at all once the ride is over.
const POLL_MS = 30000;

// The thread is re-read faster than the trip, but only while the chat is open -
// a conversation needs to feel current, a ride status does not.
const MESSAGE_POLL_MS = 8000;

export default {
  name: "UberTripView",
  components: { UberConfirm },
  data() {
    return {
      trip: null,
      loading: true,
      error: "",
      timer: null,
      callingDirect: false,
      callingUber: false,
      cancelling: false,
      chatOpen: false,
      messages: [],
      messageDraft: "",
      messagesLoading: false,
      messageTimer: null,
      messagesSending: false,
      issueOpen: false,
      issueForm: { issueType: "", description: "" },
      submittingIssue: false,
    };
  },
  computed: {
    uberTripId() {
      return this.$route.params.uberTripId;
    },
    /** Whether a car is still coming, which most of this page is about. */
    rideAhead() {
      return !!(this.trip && hasLiveTracking(this.trip.status));
    },

    /**
     * Raising a complaint, which is the one thing left to do once the ride is
     * over. The same gate the portal uses, so the two cannot diverge.
     */
    reportable() {
      return !!(this.trip && canReportIssue(this.trip.status));
    },
    issueTypes() {
      return ISSUE_TYPES;
    },
    canSubmitIssue() {
      if (this.submittingIssue) return false;
      if (!this.issueForm.issueType) return false;
      return !!this.issueForm.description.trim();
    },
    statusNarrative() {
      const status = (this.trip && this.trip.status) || "";
      const lines = {
        pending: "Your booking is being confirmed.",
        processing: "Finding a driver for you.",
        scheduled: "Booked. A driver is assigned closer to the pickup time.",
        reserve: "Reserved. Your car is guaranteed for the pickup time.",
        accepted: "A driver is on the way.",
        driver_redispatched: "A new driver has been assigned.",
        arriving: "Your driver is arriving now.",
        in_progress: "You are on your way.",
        completed: "This ride is complete.",
        no_drivers_available: "No driver was available. The team is arranging another car.",
        driver_canceled: "The driver cancelled. The team is arranging another car.",
      };
      return lines[status] || "";
    },
    hasDriverAssigned() {
      return !!(this.trip && this.trip.driver && this.trip.driver.name && this.rideAhead);
    },
    driverDetailsVisible() {
      return !!(this.trip && shouldShowDriverDetails(this.trip.status));
    },
    /**
     * Keyed on the status rather than on hasDriverAssigned above: the driver's
     * name can lag the status by a webhook, and the PIN is useful as soon as
     * Uber says a driver is coming.
     */
    showPin() {
      return !!(
        this.trip &&
        this.trip.guest &&
        this.trip.guest.otp &&
        driverAssignedStatus(this.trip.status)
      );
    },
    /** Uber's message endpoint needs a live ride, so this ends before completed. */
    canMessage() {
      return this.hasDriverAssigned && canMessageDriver(this.trip.status);
    },
    cancellable() {
      return !!(this.trip && isCancellable(this.trip.status));
    },
    vehicleDisplay() {
      const vehicle = (this.trip && this.trip.vehicle) || {};
      return `${vehicle.make || ""} ${vehicle.model || ""}`.trim() || "Vehicle";
    },
    guestInitials() {
      const name = (this.trip && this.trip.guest && this.trip.guest.name) || "";
      return (
        name
          .split(" ")
          .filter(Boolean)
          .slice(0, 2)
          .map((part) => part[0])
          .join("")
          .toUpperCase() || "?"
      );
    },
  },
  watch: {
    /*
     * The dialog closes by more routes than the Close button - the backdrop and
     * Esc both clear chatOpen through v-model. Watching the flag is the only
     * place that catches all of them, so the poll can never outlive the dialog.
     */
    chatOpen(open) {
      if (!open) this.stopMessagePoll();
    },
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    this.stopPolling();
    this.stopMessagePoll();
  },
  methods: {
    statusLabel,

    async load() {
      try {
        const response = await axios.post("uber/guest/trip-view", {
          uberTripId: this.uberTripId,
        });
        this.trip = response.data || null;
        this.schedulePoll();
      } catch (err) {
        const data = err && err.response && err.response.data;
        this.error = (data && data.message) || "";
        // A refresh failure must not blank a page already showing a ride.
      } finally {
        this.loading = false;
      }
    },

    /** Polls only while there is still a car coming. */
    schedulePoll() {
      this.stopPolling();
      if (this.rideAhead) this.timer = setTimeout(() => this.load(), POLL_MS);
    },
    stopPolling() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = null;
    },

    /* ---------------------------------------------------------------- *
     * Actions
     * ---------------------------------------------------------------- */

    /**
     * Dials the driver from this device. The passenger's own number shows on
     * the driver's phone, unlike the Uber call - the confirmation says so.
     */
    async callDirect() {
      const phone = this.trip && this.trip.driver && this.trip.driver.phoneNumber;
      if (!phone) return;

      const confirmed = await this.$refs.confirm.open(
        "Direct Call",
        "You are about to call the driver directly. Your phone number will be visible to them. Continue?",
        { icon: "fas fa-phone", color: "#111111" }
      );
      if (!confirmed) return;

      this.callingDirect = true;
      window.location.href = `tel:${phone}`;
      this.callingDirect = false;
    },

    /** Uber rings this booking's own number and bridges it to the driver. */
    async callViaUber() {
      const confirmed = await this.$refs.confirm.open(
        "Uber Call",
        "Uber will call you, then connect you to the driver. Your number is not shared.",
        { icon: "fas fa-phone", color: "#111111" }
      );
      if (!confirmed) return;

      this.callingUber = true;
      try {
        const response = await axios.post("uber/guest/call", { uberTripId: this.uberTripId });
        toast.success((response.data && response.data.message) || "Call requested.");
      } catch (err) {
        toast.error(this.readError(err, "Could not place the call."));
      } finally {
        this.callingUber = false;
      }
    },

    async cancelRide() {
      const confirmed = await this.$refs.confirm.open(
        "Confirm Uber Ride Cancellation",
        "Are you sure you want to cancel this Uber trip?",
        { icon: "fas fa-times-circle", color: "#000000" }
      );
      if (!confirmed) return;

      this.cancelling = true;
      try {
        const response = await axios.post("uber/guest/cancel", { uberTripId: this.uberTripId });
        toast.success((response.data && response.data.message) || "Ride cancelled.");
        this.load();
      } catch (err) {
        toast.error(this.readError(err, "Could not cancel this ride."));
      } finally {
        this.cancelling = false;
      }
    },

    /** Opens the report form, blank each time rather than holding the last one. */
    openIssue() {
      this.issueForm = { issueType: "", description: "" };
      this.issueOpen = true;
    },

    /**
     * Records a complaint about the finished ride.
     *
     * Nothing reaches Uber - this is taSki's own log, the same row an agent's
     * Report Issue writes from the portal.
     */
    async submitIssue() {
      if (!this.canSubmitIssue) return;

      this.submittingIssue = true;
      try {
        const response = await axios.post("uber/guest/report-issue", {
          uberTripId: this.uberTripId,
          issueType: this.issueForm.issueType,
          description: this.issueForm.description.trim(),
        });
        toast.success((response.data && response.data.message) || "Thanks - your issue has been recorded.");
        this.issueOpen = false;
      } catch (err) {
        toast.error(this.readError(err, "Could not record your report."));
      } finally {
        this.submittingIssue = false;
      }
    },

    async openChat() {
      this.chatOpen = true;
      this.messagesLoading = true;
      try {
        await this.loadMessages();
        this.$nextTick(this.scrollChat);
      } finally {
        this.messagesLoading = false;
        this.scheduleMessagePoll();
      }
    },

    closeChat() {
      this.chatOpen = false;
      this.stopMessagePoll();
    },

    /**
     * Fetches the thread and merges it in.
     *
     * Merged rather than replaced, keyed on messageId: a poll landing while
     * someone is typing must not disturb the composer, and a message this page
     * has just sent optimistically must not vanish and reappear.
     *
     * Returns whether anything new arrived, so the caller only scrolls when
     * there is something to scroll to.
     */
    async loadMessages() {
      try {
        const response = await axios.post("uber/guest/messages", { uberTripId: this.uberTripId });
        const incoming = (response.data && response.data.messages) || [];

        const seen = new Set(this.messages.map((m) => m.messageId).filter(Boolean));
        const fresh = incoming.filter((m) => !m.messageId || !seen.has(m.messageId));
        if (fresh.length === 0) return false;

        this.messages = this.messages.concat(fresh);
        return true;
      } catch (err) {
        // A failed poll is not worth a toast on the crew's phone - the next one
        // will pick the thread up.
        return false;
      }
    },

    /**
     * Re-reads the thread while the chat is open.
     *
     * This page has no socket: it is public, so it cannot join the partner's
     * room the way the portal does. Polling stops the moment the dialog closes,
     * and never starts once the ride is over.
     */
    scheduleMessagePoll() {
      this.stopMessagePoll();
      if (!this.chatOpen || !this.rideAhead) return;

      this.messageTimer = setTimeout(async () => {
        const arrived = await this.loadMessages();
        if (arrived) this.$nextTick(this.scrollChat);
        this.scheduleMessagePoll();
      }, MESSAGE_POLL_MS);
    },

    stopMessagePoll() {
      if (this.messageTimer) clearTimeout(this.messageTimer);
      this.messageTimer = null;
    },

    async sendMessage() {
      const text = this.messageDraft.trim();
      if (!text || this.messagesSending) return;
      this.messagesSending = true;
      try {
        const response = await axios.post("uber/guest/message", {
          uberTripId: this.uberTripId,
          message: text,
        });
        // The server returns the whole thread, so there is nothing to splice.
        this.messages = (response.data && response.data.messages) || this.messages;
        this.messageDraft = "";
        this.$nextTick(this.scrollChat);
      } catch (err) {
        toast.error(this.readError(err, "Could not send the message."));
      } finally {
        this.messagesSending = false;
      }
    },

    scrollChat() {
      const el = this.$refs.chatThread;
      if (el) el.scrollTop = el.scrollHeight;
    },

    /**
     * Who wrote a line. "You" for the crew's own messages, since this is their
     * page; the portal side is named so a reply from the office is obvious.
     */
    senderLabel(sender) {
      if (sender === "driver") return "Driver";
      if (sender === "admin") return "Admin";
      return "You";
    },

    readError(err, fallback) {
      return readApiError(err, fallback);
    },

    addressOf(place) {
      if (!place) return "—";
      return place.address || "—";
    },

    /** First segment of an address, as the headline above the full one. */
    shortPlace(place) {
      const address = (place && place.address) || "";
      if (!address) return "—";
      return address.split(",")[0].trim() || address;
    },

    starString(rating) {
      const filled = Math.round(Number(rating) || 0);
      return "★".repeat(Math.min(5, filled)) + "☆".repeat(Math.max(0, 5 - filled));
    },

    formatDateTime(value) {
      if (value === null || value === undefined || value === "") return "—";
      const parsed =
        typeof value === "number" || /^\d+$/.test(String(value))
          ? moment(Number(value))
          : moment(value);
      return parsed.isValid() ? parsed.format("DD MMM, hh:mm A") : "—";
    },
  },
};
</script>

<style lang="scss" scoped>
/* Ported from the corporate portal's UserTripLinkView so the crew sees the same
   page from either portal. */
.trip-shell {
  width: 100vw;
  max-width: 100vw;
  min-height: 100vh;
  margin: 0;
  padding: 16px;
  box-sizing: border-box;
  font-family: "Uber Move", "Uber Move Text", "Helvetica Neue", Arial, sans-serif;
  color: #111111;
  background: #f5f5f5;
  overflow-y: auto;
}

@media (min-width: 768px) {
  .trip-shell {
    max-width: 900px;
    margin: 0 auto;
    padding: 20px;
    border: 1px solid #e5e5e5;
    border-radius: 18px;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
  }
}

.utv-loading,
.utv-dead {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 70vh;
  text-align: center;
}

.utv-dead-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111111;
}

.utv-dead-sub {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
}

/* Header */
.trip-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 16px;
  padding: 16px 18px;
  margin-bottom: 12px;
}

.id-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 8px;
  font-size: 12px;
  color: #111111;
  // The Uber id is a long UUID and the header is narrow on a phone.
  overflow-wrap: anywhere;
}

.id-key {
  display: inline-block;
  min-width: 52px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 10px;
  font-weight: 700;
  color: #6b7280;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #ffffff;
  color: #111111;
  border: 1px solid #ffffff;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  padding: 5px 12px;
  margin-left: -12px;
}

.pulse-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #000000;
  animation: pulse 1.4s infinite;
  flex-shrink: 0;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

// Not in the corporate view: it has Uber's product name to lead with, we have
// the trip's own state, so it is worth a plain sentence.
.status-sub {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

.header-right {
  text-align: right;
}

.otp-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #111111;
  color: #ffffff;
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.6px;
  white-space: nowrap;
}

/* Meta grid */
.meta-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 12px;
}

.meta-card {
  background: #111111;
  border: 1px solid #1f1f1f;
  border-radius: 12px;
  padding: 12px;
}

.meta-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #a3a3a3;
  margin-bottom: 4px;
}

// Sized for a full "18 Aug 2026, 06:15 PM" rather than the "5 min" this used
// to hold, so it wraps inside the card instead of stretching it.
.meta-value {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
  color: #ffffff;
}

.meta-sub {
  font-size: 11px;
  color: #a3a3a3;
  margin-top: 2px;
  // Addresses are long; the card must not stretch to fit one.
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Cards */
.card {
  background: #ffffff;
  border: 1px solid #e5e5e5;
  color: #111111;
  border-radius: 16px;
  padding: 16px 18px;
  margin-bottom: 10px;
}

.section-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  font-weight: 600;
  margin-bottom: 12px;
}

/* Route */
.route-row {
  display: flex;
  gap: 14px;
  align-items: stretch;
}

.route-line {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 3px;
  flex-shrink: 0;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot-green {
  background: #22c55e;
}

.dot-red {
  background: #ef4444;
}

.route-connector {
  flex: 1;
  width: 2px;
  background: #e5e5e5;
  margin: 5px 0;
  min-height: 24px;
  border-radius: 1px;
}

.route-stops {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.stop-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #6b7280;
  margin-bottom: 2px;
}

.stop-title {
  font-size: 14px;
  font-weight: 700;
  color: #111111;
  line-height: 1.3;
}

.stop-sub {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
  line-height: 1.4;
}

.route-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #e5e5e5;
}

.meta-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #f5f5f5;
  border: 1px solid #e5e5e5;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  color: #111111;
  font-weight: 500;
}

/* Driver */
.driver-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

// Uber returns a picture_url the corporate view renders; ours is not stored, so
// this is the same circle with a glyph in it.
.driver-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #111111;
  border: 2px solid #e5e5e5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.driver-info {
  min-width: 0;
}

.driver-name {
  font-size: 16px;
  font-weight: 700;
  color: #111111;
  // Uber's sandbox driver names are long machine strings.
  overflow-wrap: anywhere;
}

.driver-rating {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
}

.stars {
  color: #f59e0b;
  font-size: 13px;
}

.rating-val {
  font-size: 12px;
  color: #6b7280;
}

.driver-phone {
  font-size: 12px;
  color: #6b7280;
  margin-top: 3px;
}

.driver-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e5e5;
}

.driver-chat {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e5e5;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 1;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #111111;
  background: #ffffff;
  color: #111111;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

// Outlined against the Uber call's solid black: the two do different things,
// but the page stays monochrome.
.action-btn--direct {
  border-color: #111111;
  color: #111111;
}

.action-btn--uber {
  background: #111111;
  color: #ffffff;
}

.action-btn--chat {
  width: 100%;
}

/* Chat */
.chat-modal {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #111111;
  padding: 16px;
}

.chat-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.chat-modal-title {
  font-size: 15px;
  font-weight: 700;
  color: #111111;
}

.chat-modal-sub {
  font-size: 12px;
  color: #6b7280;
  overflow-wrap: anywhere;
}

.chat-modal-close {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
}

.chat-thread {
  min-height: 180px;
  max-height: 44vh;
  overflow-y: auto;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 12px;
}

.chat-loading,
.chat-empty {
  text-align: center;
  font-size: 13px;
  color: #6b7280;
  padding: 30px 10px;
}

.chat-bubble {
  max-width: 78%;
  padding: 8px 12px;
  border-radius: 12px;
  margin-bottom: 8px;
  background: #ffffff;
  border: 1px solid #e5e5e5;
}

.chat-bubble--out {
  margin-left: auto;
  background: #111111;
  border-color: #111111;

  .chat-text {
    color: #ffffff;
  }

  .chat-time,
  .chat-who {
    color: rgba(255, 255, 255, 0.6);
  }
}

.chat-who {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #9ca3af;
  margin-bottom: 3px;
}

.chat-text {
  font-size: 13px;
  color: #111111;
  white-space: pre-wrap;
  word-break: break-word;
}

.chat-time {
  font-size: 10px;
  color: #9ca3af;
  margin-top: 4px;
  text-align: right;
}

.chat-composer {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.chat-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  font-size: 13px;
  resize: none;
  outline: none;

  &:focus {
    border-color: #111111;
  }
}

.chat-send {
  padding: 0 18px;
  border-radius: 10px;
  background: #111111;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.chat-hint {
  margin-top: 8px;
  font-size: 11px;
  color: #9ca3af;
  text-align: center;
}

/* Report issue - shares the chat modal's shell, input and send button. */
.issue-types {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

// Chips rather than a select: four options fit on a phone, and a native select
// opens a picker over the very page the crew is being asked to describe.
.issue-type {
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid #e5e5e5;
  background: #ffffff;
  color: #111111;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    border-color: #111111;
  }
}

.issue-type--on {
  background: #111111;
  border-color: #111111;
  color: #ffffff;
}

.issue-text {
  width: 100%;
}

.issue-submit {
  width: 100%;
  padding: 12px;
}

.cta-cancel {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #111111;
  color: #111111;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* Vehicle */
.vehicle-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.vehicle-name {
  font-size: 15px;
  font-weight: 700;
  color: #111111;
}

.vehicle-color {
  font-size: 12px;
  color: #6b7280;
  margin-top: 3px;
}

.plate {
  display: inline-block;
  background: #111111;
  color: #ffffff;
  border: 1px solid #111111;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 8px;
  margin-top: 6px;
  letter-spacing: 1px;
}

/* Passenger */
.guest-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.guest-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #111111;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

.guest-name {
  font-size: 15px;
  font-weight: 600;
  color: #111111;
}

.guest-phone {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
  letter-spacing: 1px;
}

/* CTA */
.cta-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
}

.cta-track {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px;
  border-radius: 14px;
  background: #000000;
  color: #ffffff !important;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  text-decoration: none;
  transition: background 0.15s;

  &:hover {
    background: #111111;
  }

  &.disabled {
    opacity: 0.4;
    pointer-events: none;
  }
}

.request-meta {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 11px;
  color: #a3a3a3;
  padding: 0 4px 20px;
  overflow-wrap: anywhere;
}
</style>
